import { LearningCenterImpl, Section } from "designer/apps/ui/smart/learning-center/learning-center-impl";
import { GlideFC } from "lib/glide-fc";
import React from "react";
import { createPortal } from "react-dom";
import { styled } from "sharedUI/styles";

const learningCenterSections: readonly Section[] = [
    {
        name: "Start here",
        data: [
            {
                kind: "video",
                name: "Quick Start",
                youtubeEmbedID: "DrcCtSxcQbQ",
            },
            {
                kind: "video",
                name: "Tour of Glide",
                youtubeEmbedID: "ZkCfwed6DJM",
            },
            {
                kind: "video",
                name: "Data Editor",
                youtubeEmbedID: "c3d6ST9SGVI",
            },
            {
                kind: "video",
                name: "Layout - Introduction",
                youtubeEmbedID: "RKndxZCGwGo",
            },
            {
                kind: "video",
                name: "Layout - Tabs & Lists",
                youtubeEmbedID: "dmhAUiSJNVA",
            },
            {
                kind: "video",
                name: "Layout - Details Screens",
                youtubeEmbedID: "DwcLoMVemwQ",
            },
            {
                kind: "video",
                name: "Adding & Editing",
                youtubeEmbedID: "N0dQC9_Cr2U",
            },
            {
                kind: "video",
                name: "Security",
                youtubeEmbedID: "_dujWcc807c",
            },
        ],
    },
    {
        name: "Data",
        data: [
            {
                kind: "video",
                name: "Introduction to the Data Editor",
                youtubeEmbedID: "AcBir5E8apo",
            },
            {
                kind: "video",
                name: "Introduction to Relations",
                youtubeEmbedID: "PN1xxatjmF0",
            },
            {
                kind: "video",
                name: "Single Relations",
                youtubeEmbedID: "jUWP_Lula8E",
            },
            {
                kind: "video",
                name: "Multiple Relations",
                youtubeEmbedID: "0QGbNsAPhLw",
            },
            {
                kind: "video",
                name: "Multiple Categories",
                youtubeEmbedID: "SL-GekJLtTA",
            },
            {
                kind: "video",
                name: "Lookup Column",
                youtubeEmbedID: "_y_db4dg8Iw",
            },
            {
                kind: "video",
                name: "Template Column",
                youtubeEmbedID: "lJ8a0aMU2ds",
            },
            {
                kind: "video",
                name: "Single Value Column",
                youtubeEmbedID: "h31dASRgd1k",
            },
            {
                kind: "video",
                name: "Array Columns",
                youtubeEmbedID: "x_vKwWrK9wE",
            },
            {
                kind: "video",
                name: "User Specific Columns",
                youtubeEmbedID: "PK5eovZGne0",
            },
            {
                kind: "video",
                name: "If → Then → Else Column",
                youtubeEmbedID: "W-hsgR0qyzs",
            },
            {
                kind: "video",
                name: "Row IDs",
                youtubeEmbedID: "CHYC4nuTotA",
            },
        ],
    },
    {
        name: "Tutorials",
        data: [
            {
                kind: "video",
                name: "Insiders Template",
                youtubeEmbedID: "T_jxMpdSZCE",
                playlistID: "PL9fJR6JJ89mWzS2TXLVcJHyCpMXosDRVr",
            },
            {
                kind: "video",
                name: "Build your own Project Manager App",
                youtubeEmbedID: "MLZU_gqWuRg",
                playlistID: "PL9fJR6JJ89mUvp7RJxuRAxIXvB8HuKzr-",
            },
        ],
    },
    {
        name: "View more",
        icon: "st-arrow-external",
        data: "https://docs.glideapps.com",
    },
];

interface Props {
    show: boolean;
    onClose: () => void;
}

const LearningCenterStyle = styled.div`
    position: absolute;
    right: 0;
    top: 0;
    height: 100%;

    @keyframes lc-slidein {
        from {
            transform: translateX(100%);
        }

        to {
            transform: translateX(0);
        }
    }

    @keyframes lc-slideout {
        from {
            transform: translateX(0);
        }

        to {
            transform: translateX(100%);
        }
    }

    &.open {
        animation: lc-slidein ${p => p.theme.transitionBase};
        animation-fill-mode: forwards;
    }

    &.closed {
        animation: lc-slideout ${p => p.theme.transitionSlow};
        animation-fill-mode: forwards;
    }
`;

export const LearningCenter: GlideFC<Props> = p => {
    const { show, onClose } = p;

    const [render, setRender] = React.useState(show);

    React.useEffect(() => {
        if (show) {
            setRender(true);
            return undefined;
        } else {
            const timeout = window.setTimeout(() => {
                setRender(false);
            }, 240);
            return () => window.clearTimeout(timeout);
        }
    }, [show]);

    if (!render) return null;

    const portal = document.getElementById("portal");
    if (portal === null) return null;

    const result = (
        <LearningCenterStyle className={show ? "open" : "closed"}>
            <LearningCenterImpl onClose={onClose} sections={learningCenterSections} />
        </LearningCenterStyle>
    );

    return createPortal(result, portal);
};
